/** Product Release shipment list when `fe-2784-enable-product-release-path` is on. */
export const PRODUCT_RELEASE_LIST_PATH = '/shipment/product-release'

/** Product Release shipment list while `fe-2784-enable-product-release-path` is off. */
export const LEGACY_TEMPERATURE_RELEASE_LIST_PATH = '/shipment/temperature'

/** Insights Product Release dashboard when `fe-2784-enable-product-release-path` is on. */
export const INSIGHTS_PRODUCT_RELEASE_PATH = '/insights/product-release'

/** Insights Product Release dashboard while `fe-2784-enable-product-release-path` is off. */
export const LEGACY_INSIGHTS_TEMPERATURE_RELEASE_PATH = '/insights/temperature-release'

/** Public URL slug for Insights Product Release. */
export const INSIGHTS_PRODUCT_RELEASE_SLUG = 'product-release'

/**
 * Internal insights type used by filters, saved views, and Lightdash RPCs.
 * The address bar may show `product-release` when the path gate is on; this key stays stable.
 */
export const INSIGHTS_TEMPERATURE_RELEASE_TYPE = 'temperature-release'

/** Statsig gate for the Product Release list and Insights URLs. */
export const PRODUCT_RELEASE_PATH_GATE = 'fe-2784-enable-product-release-path'

/**
 * @param {object | null | undefined} statsigClient
 * @returns {boolean} True when the Product Release URL gate is enabled.
 */
export function isProductReleasePathEnabled(statsigClient) {
  return Boolean(statsigClient?.checkGate?.(PRODUCT_RELEASE_PATH_GATE))
}

/** Address used for the Product Release shipment list. */
export function getProductReleaseListPath(statsigClient) {
  return isProductReleasePathEnabled(statsigClient)
    ? PRODUCT_RELEASE_LIST_PATH
    : LEGACY_TEMPERATURE_RELEASE_LIST_PATH
}

/** The other list address. It redirects to {@link getProductReleaseListPath}. */
export function getProductReleaseListAliasPath(statsigClient) {
  return isProductReleasePathEnabled(statsigClient)
    ? LEGACY_TEMPERATURE_RELEASE_LIST_PATH
    : PRODUCT_RELEASE_LIST_PATH
}

/**
 * Nested Product Release address under the active list prefix.
 * Examples: `create`, `edit/:uuid`, `:uuid/upload`, `:uuid/upload-v2`, `details`.
 * @param {object | null | undefined} statsigClient
 * @param {...(string | undefined)} segments
 * @returns {string} Path under the active Product Release prefix.
 */
export function getProductReleaseShipmentPath(statsigClient, ...segments) {
  const tail = segments.map((segment) => String(segment)).join('/')
  return `${getProductReleaseListPath(statsigClient)}/${tail}`
}

/** True when an upload-v2 `:view` param is a Product Release shipment. */
export function isProductReleaseShipmentView(view) {
  return view === 'temperature' || view === 'product-release'
}

/** Product Release summary prefix, for example `/shipment/product-release/summary`. */
export function getProductReleaseSummaryBasePath(statsigClient) {
  return `${getProductReleaseListPath(statsigClient)}/summary`
}

/** The other summary prefix. It redirects to {@link getProductReleaseSummaryBasePath}. */
export function getProductReleaseSummaryAliasBasePath(statsigClient) {
  return `${getProductReleaseListAliasPath(statsigClient)}/summary`
}

/**
 * Product Release summary address, including optional device or review segments.
 * @param {object | null | undefined} statsigClient
 * @param {string | undefined} shipmentUuid
 * @param {...(string | undefined)} segments
 * @returns {string} Summary path for the active Product Release URL.
 */
export function getProductReleaseSummaryPath(statsigClient, shipmentUuid, ...segments) {
  const extra = segments.filter(
    (segment) => segment !== undefined && segment !== null && segment !== '',
  )
  const tail = [shipmentUuid, ...extra].map((segment) => String(segment)).join('/')
  return `${getProductReleaseSummaryBasePath(statsigClient)}/${tail}`
}

/** Search param that opens the Temperature Review with one handling unit selected. */
export const TEMPERATURE_REVIEW_HANDLING_UNIT_PARAM = 'handlingUnitId'

/**
 * Temperature Review address for a shipment, optionally with one handling unit selected on load.
 * @param {object | null | undefined} statsigClient
 * @param {string | undefined} shipmentUuid
 * @param {string | null | undefined} [handlingUnitId]
 * @returns {string} Temperature Review path, with `?handlingUnitId=` when a unit is given.
 */
export function getTemperatureReviewPath(statsigClient, shipmentUuid, handlingUnitId = null) {
  const path = getProductReleaseSummaryPath(statsigClient, shipmentUuid, 'temperature_review')
  if (!handlingUnitId) return path
  const search = new URLSearchParams({ [TEMPERATURE_REVIEW_HANDLING_UNIT_PARAM]: handlingUnitId })
  return `${path}?${search}`
}

/** Address used for Insights Product Release. */
export function getInsightsProductReleasePath(statsigClient) {
  return isProductReleasePathEnabled(statsigClient)
    ? INSIGHTS_PRODUCT_RELEASE_PATH
    : LEGACY_INSIGHTS_TEMPERATURE_RELEASE_PATH
}

/** The other Insights address. It redirects to {@link getInsightsProductReleasePath}. */
export function getInsightsProductReleaseAliasPath(statsigClient) {
  return isProductReleasePathEnabled(statsigClient)
    ? LEGACY_INSIGHTS_TEMPERATURE_RELEASE_PATH
    : INSIGHTS_PRODUCT_RELEASE_PATH
}

export function isProductReleaseListPath(pathname = '') {
  const path = String(pathname).split('?')[0].split('#')[0].toLowerCase()
  return path === PRODUCT_RELEASE_LIST_PATH || path === LEGACY_TEMPERATURE_RELEASE_LIST_PATH
}

export function normalizeInsightsRouteType(insightsType) {
  if (insightsType === INSIGHTS_PRODUCT_RELEASE_SLUG) return INSIGHTS_TEMPERATURE_RELEASE_TYPE
  return insightsType
}
